import React from "react";
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { DeleteSharp } from "@mui/icons-material";
import { DateTimePicker, LocalizationProvider, DatePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import moment from "moment";
import { notification } from "src/utils/messages";

function ScheduleEntryRow({
  entry,
  index,
  totalEntries,
  immediateHolderId,
  onUpdate,
  onRemove,
  isEditMode,
  lockAll: lockAllProp
}) {
  const isMain =
    entry?.is_main !== undefined ? String(entry.is_main) === "1" : index === 0;
  const isSent = String(entry?.notification_send_status) === "1";
  // Poll reuses this component but labels things differently
  const isPoll = entry?.p_id != undefined && entry?.n_id == undefined;
  // Per-entry lock: a sent entry allows only End Date; unsent entries stay fully
  // editable even when sibling entries of the same batch have already been sent.
  const lockAll = !!lockAllProp || isSent;

  const handleStartDateChange = (newDate) => {
    if (newDate) {
      const formatted = moment(newDate).format("DD-MM-YYYY");
      let updated = { ...entry, s_date: formatted };
      if (
        entry.e_date &&
        moment(entry.e_date, "DD-MM-YYYY").isBefore(moment(formatted, "DD-MM-YYYY"))
      ) {
        updated.e_date = "";
        notification({ type: "error", message: "End date cannot be less than start date." })();
      }
      onUpdate(index, updated);
    }
  };

  const handleEndDateChange = (newDate) => {
    if (newDate)
      onUpdate(index, { ...entry, e_date: moment(newDate).format("DD-MM-YYYY") });
  };

  const handleSendTypeChange = (e) => {
    const value = e.target.value;
    if (value === "1" && immediateTaken) {
      notification({
        type: "error",
        message: "Only one entry can be 'Send Immediately' per batch."
      })();
      return;
    }
    onUpdate(index, {
      ...entry,
      notification_send_type: value,
      n_date_time: value === "1" ? "" : entry.n_date_time
    });
  };

  const handleDateTimeChange = (newDate) => {
    if (newDate)
      onUpdate(index, {
        ...entry,
        n_date_time: moment(newDate).format("DD-MM-YYYY hh:mm A")
      });
  };

  const handleShowOnHomeChange = (e) =>
    onUpdate(index, { ...entry, show_on_home: e.target.checked ? 1 : 0 });

  const isScheduled =
    entry.notification_send_type === "0" || entry.notification_send_type === 0;
  const isImmediate = String(entry.notification_send_type) === "1";
  // This entry's own id (notification uses n_id, poll uses p_id)
  const selfId = entry.n_id ?? entry.p_id;
  // Another entry in this batch already claims the single "Send Immediately" slot.
  // immediateHolderId is batch-wide, so this still works in a sub-only edit form
  // that renders just this one entry.
  const immediateTaken =
    immediateHolderId != undefined &&
    immediateHolderId !== null &&
    String(immediateHolderId) !== String(selfId ?? "") &&
    !isImmediate;

  // Privilege parity: the trash stays active on sent entries; the backend
// rejects removal of an already-sent schedule.
const removeDisabled = totalEntries <= 1;

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        border: isMain ? "2px solid #1976d2" : "1px solid #e0e0e0",
        borderLeft: isMain ? "4px solid #1976d2" : "1px solid #e0e0e0",
        borderRadius: 2,
        backgroundColor: isMain ? "rgba(25, 118, 210, 0.04)" : "#fff",
        position: "relative"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isSent && (
            <Typography
              sx={{
                backgroundColor: "#2e7d32",
                color: "#fff",
                px: 1.5,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontWeight: 600
              }}
            >
              Sent
            </Typography>
          )}
          {isMain ? (
            <Typography
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                px: 1.5,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontWeight: 600
              }}
            >
              Main
            </Typography>
          ) : (
            <Typography
              sx={{
                backgroundColor: "#9e9e9e",
                color: "#fff",
                px: 1.5,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontWeight: 600
              }}
            >
              Sub
            </Typography>
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Schedule Entry {index + 1}
          </Typography>
          {isSent && (
            <Typography variant="caption" color="text.secondary">
              (already sent — only End Date can be changed)
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Show on Home
            </Typography>
            <Switch
              size="small"
              checked={entry.show_on_home === 1}
              onChange={handleShowOnHomeChange}
              disabled={lockAll}
            />
          </Box>
          <Tooltip
            title={
              totalEntries <= 1
                ? "Cannot remove the only entry"
                : "Remove this entry"
            }
          >
            <span>
              <IconButton
                color="error"
                onClick={() => onRemove(index)}
                disabled={removeDisabled}
                size="small"
              >
                <DeleteSharp fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              inputFormat="dd/MM/yyyy"
              value={entry.s_date ? moment(entry.s_date, "DD-MM-YYYY").toDate() : null}
              minDate={isEditMode ? undefined : new Date()}
              onChange={handleStartDateChange}
              disabled={lockAll}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  onKeyDown={(e) => e.preventDefault()}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="End Date"
              inputFormat="dd/MM/yyyy"
              value={entry.e_date ? moment(entry.e_date, "DD-MM-YYYY").toDate() : null}
              minDate={
                entry.s_date
                  ? moment(entry.s_date, "DD-MM-YYYY").toDate()
                  : isEditMode
                    ? undefined
                    : new Date()
              }
              onChange={handleEndDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  onKeyDown={(e) => e.preventDefault()}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6}>
          <RadioGroup row value={entry.notification_send_type} onChange={handleSendTypeChange}>
            <FormControlLabel
              value="0"
              control={<Radio size="small" disabled={lockAll} />}
              label="Schedule for later"
              disabled={lockAll}
            />
            <Tooltip
              title={
                immediateTaken && !isImmediate
                  ? "Another schedule entry in this batch is already set to Send Immediately"
                  : ""
              }
            >
              <span>
                <FormControlLabel
                  value="1"
                  control={<Radio size="small" disabled={lockAll || immediateTaken} />}
                  label="Send Immediately"
                  disabled={lockAll || immediateTaken}
                />
              </span>
            </Tooltip>
          </RadioGroup>
        </Grid>
        {isScheduled && (
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label={isPoll ? "Poll Send Date & Time" : "Notification Send Date & Time"}
                inputFormat="dd/MM/yyyy hh:mm a"
                value={
                  entry.n_date_time
                    ? moment(entry.n_date_time, "DD-MM-YYYY hh:mm A").toDate()
                    : null
                }
                minDateTime={isEditMode ? undefined : moment().startOf("minute").toDate()}
                onChange={handleDateTimeChange}
                disabled={lockAll}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    onKeyDown={(e) => e.preventDefault()}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
export default ScheduleEntryRow;